import {makeAutoObservable, observable} from 'mobx';

import {User} from '@/domain';
import {Match, matchSortByFavouriteAt} from '@/domain/models/Match';
import {MatchMessageType} from '@/domain/models/MatchMessage';
import {sortByCreatedAtDesc} from '@/domain/models/Model';
import {isNonNullable} from '@/shared/lib/boolean/isNonNullable';

import {favoritedStore} from './favorited';

function hasVerificationLastMessage(match: Pick<Match, 'last_messages'>) {
  return (
    match.last_messages?.some((message) => {
      switch (message.type) {
        case MatchMessageType.photoVerificationRequest:
        case MatchMessageType.photoVerificationComplete:
        case MatchMessageType.locationVerificationRequest:
        case MatchMessageType.locationVerificationComplete:
          return true;
        default:
          return false;
      }
    }) ?? false
  );
}

class MatchStore {
  readonly matches = observable.map<Match['id'], Omit<Match, 'last_messages'>>(
    undefined
  );

  activeMatchId: Match['id'] | null = null;
  favorited = favoritedStore;
  isInitialLoaded = false;
  fetchedAllOpen = false;
  fetchedAllNew = false;
  lastLoadedOpenAt = {
    lastAt: 0,
    favouriteAt: 0,
  };
  lastLoadedNewAt = 0;

  constructor() {
    makeAutoObservable(this);
  }

  get openedMatchesIds() {
    return [...this.matches.values()]
      .filter((match) => match.status !== 'NEW')
      .sort(matchSortByFavouriteAt)
      .map((match) => match.id);
  }

  get newMatchesIds() {
    return [...this.matches.values()]
      .filter((match) => match.status === 'NEW')
      .sort(sortByCreatedAtDesc)
      .map((match) => match.id);
  }

  get openMatches(): Match[] {
    return this.openedMatchesIds
      .map((matchId) => this.matches.get(matchId))
      .filter(isNonNullable);
  }

  get newMatches(): Match[] {
    return this.newMatchesIds
      .map((matchId) => this.matches.get(matchId))
      .filter(isNonNullable);
  }

  get empty(): boolean {
    return (
      this.newMatchesIds.length === 0 && this.openedMatchesIds.length === 0
    );
  }

  setInitialLoaded(value: boolean) {
    this.isInitialLoaded = value;
  }

  setFetchedAllOpen(value: boolean) {
    this.fetchedAllOpen = value;
  }

  setFetchedAllNew(value: boolean) {
    this.fetchedAllNew = value;
  }

  setActiveMatchId(matchId: Match['id'] | null) {
    this.activeMatchId = matchId;
  }

  private normalizeMatch(match: Match): Match {
    if (match.status === 'NEW' && hasVerificationLastMessage(match)) {
      return {
        ...match,
        status: 'OPEN',
      };
    }

    return match;
  }

  private addMatches(matches: Match[]) {
    this.matches.merge(
      matches.map((match) => {
        const normalizedMatch = this.normalizeMatch(match);

        return [normalizedMatch.id, normalizedMatch] as const;
      })
    );
  }

  loadedOpenMatches(openMatches: Match[]) {
    if (openMatches.length === 0) {
      return;
    }

    const lastMatch = openMatches[openMatches.length - 1];
    this.lastLoadedOpenAt.lastAt = lastMatch.last_at;
    this.lastLoadedOpenAt.favouriteAt = lastMatch.favourite_at ?? 0;

    this.addMatches(openMatches);
  }

  loadedNewMatches(newMatches: Match[]) {
    if (newMatches.length === 0) {
      return;
    }

    const lastMatch = newMatches[newMatches.length - 1];
    this.lastLoadedNewAt = lastMatch.last_at;

    this.addMatches(newMatches);
  }

  addMatch(match: Match) {
    const normalizedMatch = this.normalizeMatch(match);

    this.matches.set(normalizedMatch.id, normalizedMatch);
  }

  deleteMatch(id: Match['id']) {
    this.matches.delete(id);
  }

  updateMatch(matchId: number, updater: (match: Match) => void) {
    const match = this.matches.get(matchId);
    if (!match) {
      return;
    }

    updater(match);
  }

  updateMatches(updater: (match: Match) => void) {
    this.matches.forEach(updater);
  }

  getMatchById(matchId: number): Match | undefined {
    return this.matches.get(matchId);
  }

  getMatchByUserId(userId: User['id']) {
    return [...this.matches.values()].find((match) => match.user_id === userId);
  }

  setFavourite(matchId: number, favouriteAt?: number) {
    const match = this.matches.get(matchId);

    if (!match) {
      return;
    }

    match.favourite_at = favouriteAt;
  }

  reset() {
    this.matches.clear();
    this.lastLoadedOpenAt = {
      lastAt: 0,
      favouriteAt: 0,
    };
    this.lastLoadedNewAt = 0;

    this.setFetchedAllOpen(false);
    this.setFetchedAllNew(false);
    this.setInitialLoaded(false);
  }
}

export default new MatchStore();
